import { Shield, Cpu, Users, Building2, FileText, Globe, Zap, BarChart3, Lock, Server, ArrowLeft, Download, Mail, MapPin, Brain, Sparkles, Rocket, Code, Layers, CheckCircle } from 'lucide-react';
import { Logo } from '../Logo';

/* ═══════════════════════════════════════════════
   D3 Global Holdings LLC - Capability Statement
   Government Contracting Page (SAM.gov Registered)
   Design matched to app.d3command.com/capability-statement
   ═══════════════════════════════════════════════ */

const coreCompetencies = {
  platform: {
    title: 'AI-Powered Platform (D3 Command)',
    items: [
      'AI Meeting Intelligence — Automated transcription, task extraction, risk detection',
      'Real-Time Operational Dashboards — Live visibility into tasks, risks, and performance',
      'Risk Detection & Compliance — AI-flagged risks with audit logging and GDPR compliance',
      'Multi-Tenant Enterprise Architecture — Complete data isolation, RBAC, organizational controls',
      'Stripe Billing Infrastructure — Subscription management with tiered plans',
    ],
  },
  services: {
    title: 'Custom Development Services',
    items: [
      'AI Workflow Automation — AI-driven workflows that reduce manual effort',
      'Enterprise Application Modernization — Legacy systems to modern platforms',
      'Operations & Governance Platforms — Execution tracking, accountability, audit readiness',
      'Dashboard & Analytics Systems — Real-time reporting for leadership teams',
      'API & Systems Integration — Unified workflows across CRMs, tools, and data sources',
      'Compliance-Ready Workflow Solutions — Role-based access and workflow traceability',
      'Public Sector & Government Solutions — Secure, process-driven software',
    ],
  },
};

const differentiators = [
  { icon: Brain, title: 'AI-Driven Insights', desc: 'Real-time intelligence that surfaces risks and patterns' },
  { icon: Shield, title: 'Built for Complex Environments', desc: 'Enterprise and government-grade security and compliance' },
  { icon: Rocket, title: 'Rapid Deployment', desc: 'Cloud-based SaaS with minimal setup; agile custom delivery' },
  { icon: Layers, title: 'Full-Stack Capability', desc: 'Frontend, backend, AI/ML, integrations — single vendor' },
  { icon: Sparkles, title: 'Interactive Demo', desc: 'Self-serve demo with AI voiceover; evaluate without sales calls' },
  { icon: FileText, title: 'SAM.gov Registered', desc: 'Ready for federal engagement and government contracting' },
];

const industries = [
  'Enterprise Operations & Cross-Functional Teams',
  'Compliance, Risk & Audit Management',
  'Financial Services & Regulated Finance',
  'Healthcare Administration & Care Coordination',
  'Public Sector, Government & Defense Contractors',
  'Product & Engineering Teams',
];

const naicsCodes = [
  { code: '541511', desc: 'Custom Computer Programming Services' },
  { code: '541512', desc: 'Computer Systems Design Services' },
  { code: '541519', desc: 'Other Computer Related Services' },
  { code: '518210', desc: 'Data Processing, Hosting & Related Services' },
  { code: '541611', desc: 'Administrative Management Consulting' },
  { code: '541715', desc: 'R&D in Computer and Information Sciences' },
];

const engagementModels = [
  { num: '01', title: 'Platform Deployment', desc: 'Deploy D3 Command with guided onboarding and configuration' },
  { num: '02', title: 'Custom Development', desc: 'Bespoke applications, dashboards, and automation systems' },
  { num: '03', title: 'Integration & Modernization', desc: 'Connect and modernize existing tools and processes' },
  { num: '04', title: 'Advisory & Enablement', desc: 'Strategic guidance on AI adoption and transformation' },
];

const techStack = [
  { category: 'Frontend', tech: 'React, Tailwind, Framer Motion' },
  { category: 'Backend', tech: 'FastAPI (Python), Motor' },
  { category: 'Database', tech: 'MongoDB Atlas' },
  { category: 'AI/ML', tech: 'OpenAI GPT, Whisper' },
  { category: 'Payments', tech: 'Stripe' },
  { category: 'Email', tech: 'SendGrid' },
  { category: 'CRM', tech: 'HubSpot' },
  { category: 'Real-Time', tech: 'WebSocket' },
  { category: 'Security', tech: 'JWT, RBAC, Redis' },
  { category: 'Hosting', tech: 'Kubernetes' },
];

const pastPerformanceStats = [
  { value: '35+', label: 'Features Shipped' },
  { value: '6', label: 'Integrations' },
  { value: '3', label: 'Subscription Tiers' },
  { value: '100%', label: 'Test Coverage' },
];

// Section Header Component with gradient lines
const SectionHeader = ({ children }) => (
  <div className="flex items-center gap-4 mb-8">
    <div className="flex-1 h-px bg-gradient-to-r from-transparent to-emerald-500/30" />
    <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">{children}</h2>
    <div className="flex-1 h-px bg-gradient-to-l from-transparent to-emerald-500/30" />
  </div>
);

const CapabilityStatementPage = () => {
  const handleDownloadPDF = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white print:bg-white print:text-black">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          * { color: black !important; background: white !important; border-color: #ddd !important; }
          a { text-decoration: underline; }
        }
      `}</style>

      {/* Header Actions - No Print */}
      <div className="no-print sticky top-0 z-50 bg-[#0A0A0B]/95 backdrop-blur-sm border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium hover:bg-emerald-500/20 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Company Header - Split Layout */}
        <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-8 mb-16 pb-10 border-b border-white/5 print:border-gray-300">
          {/* Left Side - Logo and Title */}
          <div className="flex items-start gap-4">
            <Logo size="medium" />
            <div>
              <h1 className="text-2xl font-bold text-white">D3 Global Holdings LLC</h1>
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-emerald-400 mt-1">Capability Statement</p>
              <p className="text-xs text-zinc-500 mt-2">
                McKinney, TX &nbsp;|&nbsp; SAM Registered &nbsp;|&nbsp; UEI: THEVRTELYNB6 &nbsp;|&nbsp; CAGE Code: 1ZZK7
              </p>
            </div>
          </div>
          
          {/* Right Side - Contact Info */}
          <div className="flex flex-col items-start md:items-end gap-2 text-sm">
            <a href="mailto:support@d3command.com" className="flex items-center gap-2 text-zinc-400 hover:text-emerald-400 transition-colors">
              <Mail className="w-4 h-4 text-emerald-400" />
              support@d3command.com
            </a>
            <a href="https://www.d3command.com" className="flex items-center gap-2 text-zinc-400 hover:text-emerald-400 transition-colors">
              <Globe className="w-4 h-4 text-emerald-400" />
              www.d3command.com
            </a>
            <a href="https://app.d3command.com" className="flex items-center gap-2 text-zinc-400 hover:text-emerald-400 transition-colors">
              <Server className="w-4 h-4 text-emerald-400" />
              app.d3command.com
            </a>
            <span className="flex items-center gap-2 text-zinc-400">
              <Users className="w-4 h-4 text-emerald-400" />
              Seun Olawepo, Founder & CEO
            </span>
          </div>
        </header>

        {/* Company Overview */}
        <section className="mb-16">
          <SectionHeader>Company Overview</SectionHeader>
          <p className="text-zinc-400 leading-relaxed text-sm">
            D3 Global Holdings LLC is a U.S.-based technology company specializing in AI-powered enterprise software, 
            operational intelligence, and custom software development. Our flagship platform, D3 Command, transforms 
            meetings, workflows, and operational data into execution, visibility, and compliance-ready outcomes. We 
            serve enterprise, public sector, and government clients with production-ready SaaS solutions and custom 
            development services designed for complex, regulated environments.
          </p>
        </section>

        {/* Core Competencies */}
        <section className="mb-16">
          <SectionHeader>Core Competencies</SectionHeader>
          <div className="grid md:grid-cols-2 gap-6">
            {/* AI Platform */}
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <h3 className="font-semibold mb-4 text-emerald-400 text-sm">{coreCompetencies.platform.title}</h3>
              <ul className="space-y-2">
                {coreCompetencies.platform.items.map((item, i) => (
                  <li key={i} className="text-sm text-zinc-400 flex items-start gap-2">
                    <span className="text-emerald-400 mt-0.5">-</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            {/* Custom Services */}
            <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
              <h3 className="font-semibold mb-4 text-emerald-400 text-sm">{coreCompetencies.services.title}</h3>
              <ul className="space-y-2">
                {coreCompetencies.services.items.map((item, i) => (
                  <li key={i} className="text-sm text-zinc-400 flex items-start gap-2">
                    <span className="text-emerald-400 mt-0.5">-</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Differentiators */}
        <section className="mb-16">
          <SectionHeader>Differentiators</SectionHeader>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {differentiators.map((diff, i) => (
              <div key={i} className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                <diff.icon className="w-5 h-5 text-emerald-400 mb-3" />
                <h3 className="font-semibold text-sm text-white mb-1">{diff.title}</h3>
                <p className="text-xs text-zinc-500">{diff.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Industries & NAICS */}
        <section className="mb-16 grid md:grid-cols-2 gap-12">
          <div>
            <SectionHeader>Industries Served</SectionHeader>
            <ul className="space-y-2">
              {industries.map((ind, i) => (
                <li key={i} className="text-sm text-zinc-400">{ind}</li>
              ))}
            </ul>
          </div>
          <div>
            <SectionHeader>NAICS Codes</SectionHeader>
            <ul className="space-y-2">
              {naicsCodes.map((naics, i) => (
                <li key={i} className="text-sm flex items-baseline gap-2">
                  <span className="font-mono text-emerald-400 font-semibold">{naics.code}</span>
                  <span className="text-zinc-500">{naics.desc}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Engagement Models */}
        <section className="mb-16">
          <SectionHeader>Engagement Models</SectionHeader>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {engagementModels.map((model) => (
              <div key={model.num} className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                <span className="text-2xl font-bold text-emerald-400/20">{model.num}</span>
                <h3 className="font-semibold text-sm mt-2 text-white">{model.title}</h3>
                <p className="text-xs text-zinc-500 mt-1">{model.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Technology Stack */}
        <section className="mb-16">
          <SectionHeader>Technology Stack</SectionHeader>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {techStack.map((item, i) => (
              <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/5 text-center">
                <p className="text-xs text-emerald-400 font-semibold mb-1">{item.category}</p>
                <p className="text-xs text-zinc-400">{item.tech}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Past Performance */}
        <section className="mb-16">
          <SectionHeader>Past Performance</SectionHeader>
          <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5">
            <h3 className="font-semibold mb-4 text-white text-sm">D3 Command Platform — Flagship Product</h3>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              {pastPerformanceStats.map((stat, i) => (
                <div key={i} className="text-center p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <p className="text-2xl font-bold text-emerald-400">{stat.value}</p>
                  <p className="text-xs text-zinc-500">{stat.label}</p>
                </div>
              ))}
            </div>
            
            <p className="text-sm text-zinc-400 leading-relaxed">
              Full AI pipeline: audio transcription, task/risk extraction, real-time dashboards. Integrations with 
              Stripe, SendGrid, HubSpot, Zoom, Google Meet, and Microsoft Teams. Multi-tenant architecture with RBAC, 
              audit logging, and GDPR compliance. Interactive demo available at{' '}
              <a href="https://app.d3command.com/demo" className="text-emerald-400 hover:underline">app.d3command.com/demo</a>.
            </p>
          </div>
        </section>

        {/* Contact Footer - Split Layout */}
        <footer className="pt-10 border-t border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          {/* Left Side */}
          <div>
            <h2 className="text-lg font-bold text-white">D3 Global Holdings LLC</h2>
            <p className="text-xs text-zinc-500 mt-1">
              McKinney, TX &nbsp;|&nbsp; SAM.gov Active &nbsp;|&nbsp; UEI: THEVRTELYNB6
            </p>
          </div>
          
          {/* Right Side - Links */}
          <div className="flex flex-wrap items-center gap-6 text-sm">
            <a href="mailto:support@d3command.com" className="flex items-center gap-2 text-zinc-400 hover:text-emerald-400 transition-colors">
              <Mail className="w-4 h-4 text-emerald-400" />
              support@d3command.com
            </a>
            <a href="https://www.d3command.com" className="flex items-center gap-2 text-zinc-400 hover:text-emerald-400 transition-colors">
              <Globe className="w-4 h-4 text-emerald-400" />
              www.d3command.com
            </a>
            <a href="https://app.d3command.com/demo" className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors font-medium">
              <Sparkles className="w-4 h-4" />
              Live Demo
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default CapabilityStatementPage;
